import {
    useReactTable,
    getCoreRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    ColumnDef,
    flexRender,
    SortingState,
} from "@tanstack/react-table";
import { Image } from "react-bootstrap";
import { ASSESTS } from "../../../../../constant/assest";
import CustomButton, { ButtonFont, ButtonSize, ButtonType } from "../../../../../component/common/CustomButton/CustomButton";
import cn from 'classnames';
import UserCard from "../../../../../component/common/UserCard/UserCard";
import React, { useEffect, useMemo, useState, useRef } from "react";
import Pagination from "../../../../../component/UI/Pagination/Pagination";
import { IInactiveUser } from "../../../../../redux/slices/campaigns/types";
import TableSkeleton from "../../../../../component/common/TableSkeleton";
import { CampaignTabs, CampaignType } from "../../../../../constant/enums";
import { usePagination } from "../../../../../hooks/usePagination";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../../../redux/store";
import { fetchInactiveCatchUsers, fetchInactiveInvitee, sendManualNotification } from "../../../../../redux/slices/campaigns/actions";
import ConfirmationModal from "../../../../../component/common/ConfirmationModal/ConfirmationModal";
import styles from './CampaignsTable.module.css';
import { campaignTypeOptions } from "../../../../../component/UI/CampaignsForm/CampaignsForm";
import { clearMessage, clearError  } from '../../../../../redux/slices/campaigns/slice';
import { toast } from 'react-toastify';

interface CampaignsTableProps {
    activeTab: CampaignTabs;
    search: string;
}

const CampaignsTable = ({ activeTab, search }: CampaignsTableProps) => {
    const dispatch = useDispatch<AppDispatch>();
    const { inactiveCatchUsersList, inactiveInviteeList, inactiveCatchUsersLoading, inactiveInviteeLoading, sendingNotification, notificationSentMessage, notificationSentError } = useSelector((state: RootState) => state.campaigns);
    const [showNotificationPopup, setShowNotificationPopup] = useState(false);
    const [selectedUser, setSelectedUser] = useState<IInactiveUser | null>(null);
    const [notifiedUserId, setNotifiedUserId] = useState<number | null>(null);
    const tab = campaignTypeOptions.find((type)=> type.label=== activeTab)?.value || CampaignType.CATCH;

    const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 5 });
    const [sorting, setSorting] = useState<SortingState>([]);

    const skipNextApiCall = useRef(false);

    // Reset pagination on search OR tab change
    useEffect(() => {
        skipNextApiCall.current = true;
        setPagination(old => ({ ...old, pageIndex: 0 }));
    }, [search, activeTab]);

    // Single API caller
    useEffect(() => {
        if (skipNextApiCall.current) {
            skipNextApiCall.current = false;
            return;
        }

        const params = {
            page: pagination.pageIndex + 1,
            limit: pagination.pageSize,
            name: search || undefined,
        };

        if (activeTab === CampaignTabs.CATCH) {
            dispatch(fetchInactiveCatchUsers(params));
        } else {
            dispatch(fetchInactiveInvitee(params));
        }

    }, [dispatch, pagination, search, activeTab]);


    const { data, total, totalPages } = useMemo(() => {
        const tableData = activeTab === CampaignTabs.CATCH ? inactiveCatchUsersList : inactiveInviteeList;
        return {
            data: tableData?.data || [],
            total: Number(tableData?.total) || 0,
            totalPages: tableData?.totalPages || 0,
        };
    }, [activeTab, inactiveCatchUsersList, inactiveInviteeList]);

    const handleBellClick = (user: IInactiveUser) => {
        setSelectedUser(user);
        setShowNotificationPopup(true);
    };

    const handleSubmitNotification = () => {
        if (selectedUser) {
            dispatch(sendManualNotification({ user_id: Number(selectedUser.id), tab }))
                .unwrap()
                .then(() => {
                    setNotifiedUserId(Number(selectedUser.id));
                    setShowNotificationPopup(false);
                    setSelectedUser(null);
                    setTimeout(() => {
                        setNotifiedUserId(null);
                    }, 1000);
                })
                .catch((err) => {
                    console.error('Failed to send notification:', err);
                    setShowNotificationPopup(false);
                    setSelectedUser(null);
                });
        }
    };

    useEffect(()=> {
        if(notificationSentMessage){
            toast.success(notificationSentMessage);
            dispatch(clearMessage());
        }
        if(notificationSentError){
            toast.error(notificationSentError);
            dispatch(clearError());
        }
    }, [dispatch, notificationSentMessage, notificationSentError])

    const loading = inactiveCatchUsersLoading === 'pending' || inactiveInviteeLoading === 'pending';

    const columns = useMemo<ColumnDef<IInactiveUser>[]>(() => [
        {
            header: "User",
            accessorKey: "name",
            cell: ({ row }) => (
                <UserCard
                    userName={row.original.name || 'N/A'}
                    userImage={row.original.profile_image}
                />
            ),
            sortingFn: (rowA, rowB, columnId) => {
                const a = rowA.getValue<string>(columnId) || '';
                const b = rowB.getValue<string>(columnId) || '';
                return a.localeCompare(b);
            },
        },
        {
            header: "Email",
            accessorKey: "email",
            cell: ({ getValue }) => getValue<string>() || 'N/A',
        },
        {
            header: `Days Inactive - ${activeTab}`,
            accessorKey: 'since',
            cell: ({ getValue }) => getValue<number>() ?? 'N/A',
        },
        {
            header: "Action",
            accessorKey: "action",
            cell: ({ row }) => {
                const isNotifying = sendingNotification === 'pending' && selectedUser?.id === row.original.id;
                const isNotified = notifiedUserId === Number(row.original.id);
                return (
                    <CustomButton
                        btnType={ButtonType.IMAGE}
                        onClick={() => handleBellClick(row.original)}
                        disabled={isNotifying}
                    >
                        <Image src={ASSESTS.BELLG} alt={'Ring'} width={30} height={30} className={isNotified ? styles.bellAnimation : ''} />
                    </CustomButton>
                );
            },
            enableSorting: false,
        },
    ], [activeTab, sendingNotification, selectedUser, notifiedUserId]);

    const table = useReactTable({
        data,
        columns,
        state: { sorting, pagination },
        onSortingChange: setSorting,
        onPaginationChange: setPagination,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        manualPagination: true,
        pageCount: totalPages,
        sortDescFirst: false,
    });

    const paginationGroup = usePagination({
        currentPageIndex: pagination.pageIndex,
        totalPages: totalPages,
    });

    const pageNumbersElements = paginationGroup.map((page, index) => {
        if (typeof page === 'string') {
            return <span key={`ellipsis-${index}`} className={'pageNumberBtn'} style={{ cursor: 'default' }}>{page}</span>;
        }

        const pageIndex = page;
        return (
            <CustomButton
                key={pageIndex}
                onClick={() => table.setPageIndex(pageIndex)}
                disabled={pagination.pageIndex === pageIndex}
                btnType={
                    pagination.pageIndex === pageIndex
                        ? ButtonType.ACTIVENOSTYLE
                        : ButtonType.NOSTYLE
                }
                btnSize={ButtonSize.NORMAL}
                btnFont={
                    pagination.pageIndex === pageIndex
                        ? ButtonFont.BOLD
                        : ButtonFont.NORMAL
                }
                className={cn(
                    'pageNumberBtn',
                    pagination.pageIndex === pageIndex && 'activePageNumberBtn'
                )}
            >
                {pageIndex + 1}
            </CustomButton>
        );
    });
    
    return (
        <>
            <div className={'tableContainer'}>
                <div className={'tableWrapper'}>
                    <table>
                        <thead>
                            {table.getHeaderGroups().map((headerGroup) => (
                                <tr key={headerGroup.id}>
                                    {headerGroup.headers.map((header) => {
                                        const isActionColumn =
                                            typeof header.column.columnDef.header === "string" &&
                                            header.column.columnDef.header === "Action";
                                        return (
                                            <th
                                                key={header.id}
                                                onClick={header.column.getToggleSortingHandler()}
                                                style={{
                                                    cursor: header.column.getCanSort() ? "pointer" : "default"
                                                }}
                                                className={cn(
                                                    header.column.columnDef.id && `th-${header.column.columnDef.id}`,
                                                    isActionColumn ? 'text-center' : undefined
                                                )}
                                            >
                                                {flexRender(header.column.columnDef.header, header.getContext())}
                                                {header.column.getCanSort() &&
                                                    <span className={cn('tableShorting')}>
                                                        <Image
                                                            src={header.column.getIsSorted() === "asc"
                                                                ? ASSESTS.ASSENDING
                                                                : header.column.getIsSorted() === "desc"
                                                                    ? ASSESTS.DECENDING
                                                                    : ASSESTS.SHORT}
                                                            alt="Short"
                                                        />
                                                    </span>
                                                }
                                            </th>
                                        );
                                    })}
                                </tr>
                            ))}
                        </thead>
                        <tbody>
                            {loading ? (
                                <TableSkeleton
                                    columns={columns.length}
                                    rows={pagination.pageSize}
                                />
                            ) : table.getRowModel().rows.length === 0 ? (
                                <tr>
                                    <td colSpan={columns.length} className={'noResultsFound'}>
                                        No results found
                                    </td>
                                </tr>
                            ) : table.getRowModel().rows.map((row) => (
                                <tr key={row.id}>
                                    {row.getVisibleCells().map((cell) => {
                                        const isActionColumn =
                                            typeof cell.column.columnDef.header === "string" &&
                                            cell.column.columnDef.header === "Action";

                                        return (
                                            <td
                                                key={cell.id}
                                                className={cn(
                                                    cell.column.columnDef.id && `td-${cell.column.columnDef.id}`,
                                                    isActionColumn ? 'text-center' : undefined
                                                )}
                                            >
                                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                            </td>
                                        );
                                    })}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <Pagination
                    enteriesShows={`${total
                        ? pagination.pageIndex * pagination.pageSize + 1
                        : 0
                        }-${Math.min(
                            (pagination.pageIndex + 1) * pagination.pageSize,
                            total || 0
                        )} of ${total || 0} Entries`}

                    pageNumbers={[
                        totalPages > 1 && (
                            <CustomButton
                                key="prev"
                                onClick={() => table.previousPage()}
                                disabled={!table.getCanPreviousPage()}
                                btnType={ButtonType.NOSTYLE}
                                className={'pageNumberBtn'}
                            >
                                Prev
                            </CustomButton>
                        ),
                        ...pageNumbersElements,
                        totalPages > 1 && (
                            <CustomButton
                                key="next"
                                onClick={() => table.nextPage()}
                                disabled={!table.getCanNextPage()}
                                btnType={ButtonType.NOSTYLE}
                                className={'pageNumberBtn'}
                            >
                                Next
                            </CustomButton>
                        ),
                    ].filter(Boolean)}

                    dropDownOptions={[
                        // { value: "2", label: "2" },
                        { value: "5", label: "5" },
                        { value: "10", label: "10" },
                        { value: "20", label: "20" },
                        { value: "25", label: "25" },
                    ]}
                    dropdownOnChange={(value) => {
                        setPagination(old => ({ ...old, pageSize: Number(value), pageIndex: 0 }));
                    }}
                    dropDownPlaceholder={pagination.pageSize.toString()}
                />
            </div>
            <ConfirmationModal
                title={'Send Notification'}
                showModal={showNotificationPopup}
                onClose={() => {
                    setShowNotificationPopup(false);
                    setSelectedUser(null);
                }}
                bodyText={'Are you sure you want to send Notification to the user?'}
                cancelBtnText='Cancel'
                submitBtnText={sendingNotification === 'pending' ? 'Sending...' : 'Send'}
                submitBtnType={ButtonType.GREEN}
                onSubmit={handleSubmitNotification}
                isSubmitDisabled={sendingNotification === 'pending'}
            />
        </>
    );
};

export default React.memo(CampaignsTable);
